import { useCallback, useMemo } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { map } from 'lodash-es';
import { SubmitHandler, useFormContext } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { bambiApi } from 'src/common/external/bambi-api/bambiApi';
import { useRoutesRouteBambiRouteCreateMutation } from 'src/common/external/bambi-api/routesApi';
import { ModalFooter } from 'src/common/primitives/Modal/ModalFooter';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { routeTemplateActions } from 'src/features/trip/routeTemplates/routeTemplates.slice';

import { RouteTemplatesDataGrid } from '../datagrid/RouteTemplatesDataGrid';
import { RouteTemplatesFilters } from '../datagrid/RouteTemplatesFilters';
import { ApplyRouteTemplateForm } from '../types';

interface ApplyRouteTemplatesStep1Props {
  onCancel: () => void;
  onNext: () => void;
}
export function ApplyRouteTemplatesStep1({
  onCancel,
  onNext,
}: ApplyRouteTemplatesStep1Props) {
  const selectedUnassignedTripIds = useAppSelector(
    (state) => state.dispatcher.selectedUnassignedTripIds
  );
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const assignments = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assignments
  );

  const unassignedTripIds = useMemo(() => {
    if (selectedUnassignedTripIds.length > 0) {
      return selectedUnassignedTripIds;
    }

    return unassignedTrips?.map((trip) => trip.id) ?? [];
  }, [selectedUnassignedTripIds, unassignedTrips]);

  const form = useFormContext<ApplyRouteTemplateForm>();
  const selectedRouteTemplates = Object.entries(
    form.watch('selectedRouteTemplatesMap')
  )
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const [routeBambiRoute, { isLoading }] =
    useRoutesRouteBambiRouteCreateMutation();

  const dispatch = useAppDispatch();

  const handleSubmit = useCallback<SubmitHandler<ApplyRouteTemplateForm>>(
    async (data) => {
      const routeTemplateIds = map(
        data.selectedRouteTemplatesMap,
        (value, key) => {
          if (value) {
            return key;
          }
        }
      ).filter(Boolean) as string[];

      try {
        const result = await routeBambiRoute({
          applyRoutesRequest: {
            assignment_ids: assignments?.map((a) => a.id) ?? [],
            route_template_ids: routeTemplateIds,
            trip_ids: unassignedTripIds,
          },
        }).unwrap();

        dispatch(routeTemplateActions.setApplyRouteTemplatesResult(result));
        // Manually invalidate dispatch and trips tags to ensure the new routes
        // are fetched, typically this would be done using the enhancedBambiApi,
        // but we're unable to because the api files are split.
        dispatch(bambiApi.util.invalidateTags(['dispatch', 'trips']));
        onNext();
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to apply route templates',
            description: formatServerError(e),
          })
        );
      }
    },
    [dispatch, routeBambiRoute, assignments, unassignedTripIds, onNext]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  // Unlikely state, somehow the user would have no trips in the dispatch
  // response but were still able to open the modal but we'll handle it just in
  // case
  if (!unassignedTripIds.length) {
    return (
      <div className="flex items-center justify-center">
        No trips found, please select some trips to dispatch using a route
        template.
      </div>
    );
  }

  const tripCount =
    unassignedTripIds.length > 9 ? '9+' : unassignedTripIds.length;

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="text-sm text-gray-500">
        Select route templates to automatically dispatch the ({tripCount})
        selected trips.
      </div>
      <div className="flex max-h-[600px] flex-col gap-2">
        <RouteTemplatesFilters />
        <RouteTemplatesDataGrid />
      </div>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={selectedRouteTemplates.length === 0}
        >
          Apply
        </Button>
      </ModalFooter>
    </div>
  );
}
